// React.
import { useEffect, useMemo, useRef, useState } from 'react';

// Third-parties.
import * as dayjs from 'dayjs';
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { CSVLink } from 'react-csv';

// Types.
import { Parameter } from '../../../types/instance/Parameter';

// Components.
import NoData from '../../NoData';
import TablePagination from '../../TablePagination';

// Helper.
import { getDefaultPageSize } from '../../../helpers/utils';
import { useLocation, useNavigate } from "react-router-dom";
import SearchInput from '../../SearchInput';

export default function ParameterTable (props: {
	error: string | null,
	loading: boolean,
	data: Parameter[]
})
{
	const { error, loading, data } = props;

	const navigate = useNavigate();
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const [tableFilter, setTableFilter] = useState(query.get("filter") || "");
	const initialPageIndex = Number(query.get("page") || 0);
	const initialPageSize = Number(query.get("pageSize") || getDefaultPageSize());
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus({ preventScroll: true });
		}
	}, []);

	// CSV headers.
	const headers: any[] = useMemo (() =>
	{
		return [
			{
				label: 'Parameter',
				key: 'parameter'
			},
			{
				label: 'Value',
				key: 'value'
			}
		]
	}, []);

	// Table columns.
	const columns: Column[] = useMemo(() =>
	{
		return [
			{
				Header: 'Parameter',
				accessor: 'parameter',
				headerClassName: 'text',
				canSort: true,
				sortDescFirst: false,
				sortType: 'basic'
			},
			{
				Header: 'Value',
				accessor: 'value',
				headerClassName: 'text',
				canSort: true,
				sortDescFirst: false,
				sortType: 'basic'
			}
		]
	}, []);

	const tableData = useMemo (() =>
			data.filter ((row) =>
				row.parameter.toLowerCase ().includes (tableFilter.toLowerCase ()) ||
				row.value?.toLowerCase ().includes (tableFilter.toLowerCase ())),
		[ data, tableFilter ]);

	const csvData = useMemo (() =>
	{
		// Escape embedded double-quotation marks (") in the value.
		const csvData: any[] = JSON.parse (JSON.stringify (data));

		csvData.forEach ((item) =>
		{
			if (item.value)
			{
				item.value = item.value.replaceAll ('"', '""');
			}
		});

		return csvData;
	}, [ data ]);

	const
	{
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		setPageSize,
		pageOptions,
		pageCount,
		canPreviousPage,
		previousPage,
		canNextPage,
		nextPage,
		gotoPage,
		state: { pageIndex, pageSize }
	} = useTable (
	{
		columns,
		data: tableData,
		initialState:
		{
			pageIndex: initialPageIndex,
			pageSize: initialPageSize,
		},
		disableSortRemove: true
	}, useSortBy, usePagination);

	useEffect(() => {
		const params = new URLSearchParams(location.search);

		if (tableFilter) {
			params.set('filter', tableFilter);
		} else {
			params.delete('filter');
		}

		params.set('page', pageIndex.toString());
		params.set('pageSize', pageSize.toString());

		navigate({ search: params.toString() }, { replace: true });
	}, [tableFilter, pageIndex, pageSize]);

	useEffect(() => {
		if (tableFilter) {
			gotoPage(0);
		}
	}, [tableFilter]);

	function clearTableFilter ()
	{
		setTableFilter ('');
	}


	// The bracket must be present and on the same line as the return.
	return (
		<div className="card">
			<div className="card-header">
				<i className="fal fa-scroll fa-fw" aria-hidden="true"/>
				Parameters
				<span className="badge bg-info"
					  data-tip="Total change count">{data.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
				<i className="collapse-toggle" role="button" data-bs-toggle="collapse"
				   data-bs-target="#collapse-usage" aria-expanded="false" aria-controls="collapse-usage"/>
			</div>
			<div id="collapse-usage" className="card-body collapse show">
				<div className="row row-cols-1 row-cols-md-2 table-search">
<div className="col col-md-10">
                        <CSVLink role="button"
								 headers={headers}
								 data={csvData}
								 download={`DBmarlin - ${dayjs.default().format('YYYY-MM-DD HH:mm')} - Parameters.csv`}
								 className="btn btn-sm btn-primary">
							<i className="fal fa-file-export fa-fw"/>
							<span>Export</span>
						</CSVLink>
					</div>
					<div className="col col-md-2">
						<SearchInput
							ref={inputRef}
							value={tableFilter}
							onChange={(e) => setTableFilter(e.target.value)}
							onClear={clearTableFilter}
						/>
					</div>
				</div>
				<div className="table-responsive">
					<table {...getTableProps()} className="table">
						<thead>
						{headerGroups.map((headerGroup) => (
							<tr {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map((column) => (
									<th {...column.getHeaderProps(column.getSortByToggleProps())}
										className={(column as any).headerClassName}>
										{column.render('Header')}
										{column.canSort ? (column.isSorted ? (column.isSortedDesc ?
												<i className="fal fa-sort-amount-up-alt"></i> :
												<i className="fal fa-sort-amount-down-alt"></i>) :
											<i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
									</th>))}
							</tr>))}
						</thead>
						<tbody {...getTableBodyProps()}>
						{page.map((row) => {
							prepareRow(row);
							return (
								<tr {...row.getRowProps()}>
									{row.cells.map((cell) => {
										return (
											<td {...cell.getCellProps()}
												className={(cell.column as any).className}>
												{cell.render('Cell')}
											</td>)
									})}
								</tr>)
						})}
						</tbody>
					</table>
				</div>
				<NoData error={error} loading={loading}
						length={data.length}/>
				<TablePagination
					length={data.length}
					pageSize={pageSize}
					setPageSize={setPageSize}
					pageOptions={pageOptions}
					pageCount={pageCount}
					canPreviousPage={canPreviousPage}
					previousPage={previousPage}
					canNextPage={canNextPage}
					nextPage={nextPage}
					gotoPage={gotoPage}
					pageIndex={pageIndex}
				/>
			</div>
		</div>
	)
}
